import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import ParamsEditor from './ParamsEditor';
import { typeLabelOf } from '../../nodeCatalog';
import { descendantsOf } from '../../utils/groupMembership';

interface Props { node: any }

const REF_RE = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
/** 실행 때 ${이름} 을 치환하는 노드 — 백엔드 canvas_converter.SCOPE_PARAM_TYPES 와 같다 */
const SCOPE_TYPES = new Set(['runWorkflow', 'shellCommand', 'sqlScript', 'transformLoad']);

/** 값 안의 ${이름} 을 모두 모은다 */
function collectRefs(v: any, out: Set<string>) {
  if (typeof v === 'string') { let m; REF_RE.lastIndex = 0; while ((m = REF_RE.exec(v))) out.add(m[1]); }
  else if (Array.isArray(v)) v.forEach((x) => collectRefs(x, out));
  else if (v && typeof v === 'object') Object.values(v).forEach((x) => collectRefs(x, out));
}

/**
 * 서브플로 속성 — 이름과 변수/파라미터 표.
 * 여기 정의한 이름은 서브플로 **안쪽 노드**(실행·외부명령어·SQL실행·데이터처리)의 본문에서 ${이름} 으로 쓴다.
 * 치환 순서: 실행 conf > 안쪽 노드 자신의 파라미터 > 서브플로(안쪽 서브플로가 바깥 것보다 우선) > 전역변수.
 */
const SubflowPanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow?.nodes || []);
  const cfg = node?.data?.config || {};
  const params: { name: string; value: string }[] = Array.isArray(cfg.params) ? cfg.params : [];

  const patch = useCallback((p: Record<string, any>) => {
    dispatch(updateNodeData({ id: node.id, data: { config: { ...cfg, ...p }, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  // 안쪽 노드가 참조하지만 여기에도, 그 노드 자신에도 정의되지 않은 이름 — 눌러서 바로 추가
  const undefinedRefs = useMemo(() => {
    const inner = descendantsOf(node.id, nodes);
    const refs = new Set<string>();
    nodes.forEach((n: any) => {
      if (!inner.has(n.id) || !SCOPE_TYPES.has(n.type)) return;
      const mine = new Set<string>();
      collectRefs(n.data?.config, mine);
      (n.data?.config?.params || []).forEach((p: any) => p?.name && mine.delete(p.name));
      mine.forEach((x) => refs.add(x));
    });
    params.forEach((p) => refs.delete(p.name));
    return Array.from(refs).sort();
  }, [nodes, node.id, params]);

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500">{t('property:exec.nodeType')}</span>
          <input type="text" value={typeLabelOf(String(node.type))} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} />
        </div>
      </div>
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:field.label')}</label>
        <input className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={cfg.label ?? node.data?.label ?? ''} onChange={(e) => patch({ label: e.target.value })} />
      </div>

      <ParamsEditor items={params} onChange={(items) => patch({ params: items })} />
      <div className="text-[0.6875rem] text-gray-500 -mt-1.5 mb-3">
        {t('property:subflow.hint')} <code>{'${name}'}</code> {t('property:subflow.hintTail')}
      </div>

      {undefinedRefs.length > 0 && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-2">
          <div className="text-xs text-amber-800 mb-1">{t('property:subflow.undefinedRefs')}</div>
          <div className="flex flex-wrap gap-1">
            {undefinedRefs.map((r) => (
              <button key={r} type="button" title={t('property:subflow.addRef')}
                className="px-1.5 py-0.5 text-xs font-mono rounded border border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
                onClick={() => patch({ params: [...params, { name: r, value: '' }] })}>
                + {r}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubflowPanel;
